import type { dynamicObj } from 'src/pages/assets';
import { useSettingStore } from 'src/store/main';

export interface ColumnConfig {
  name: string;
  order: number;
  [key: string]: any;
}

export interface ColumnSettingsOptions {
  settingKey: string;
  tableColumns: dynamicObj;
  defaultFilter?: (col: any) => boolean;
}

/**
 * Get column setting from server
 */
export async function getColumnFromServer(settingKey: string): Promise<string> {
  try {
    const settings_store = useSettingStore();
    const columns_setting = settings_store.user_settings[settingKey];
    return (columns_setting as string) || '';
  } catch (err) {
    return '';
  }
}

/**
 * Save columns as comma-separated string
 */
export async function saveColumnsToServer(settingKey: string, columns: any[] | dynamicObj): Promise<void> {
  const columnNames = Array.isArray(columns)
    ? columns.map((col: any) => col.name).join(',')
    : Object.keys(columns).join(',');
  const settings_store = useSettingStore();
  await settings_store.updateSetting(settingKey, columnNames);
}

/**
 * Prepare default columns from table_columns
 * @param tableColumns - The table columns definition
 * @param filterFn - Optional filter function (e.g., col => col.preview === true)
 */
export function prepareDefaultColumns(
  tableColumns: dynamicObj,
  filterFn?: (col: any) => boolean
): dynamicObj {
  const visible_columns = {} as dynamicObj;
  Object.keys(tableColumns).forEach((key) => {
    const col = tableColumns[key];
    if (!filterFn || filterFn(col)) {
      visible_columns[key] = { ...col };
    }
  });
  return visible_columns;
}

/**
 * Build visible columns from column names array
 */
function buildVisibleColumns(
  colNames: string[],
  tableColumns: dynamicObj,
  orderMap?: dynamicObj
): dynamicObj {
  const visible_columns = {} as dynamicObj;
  colNames.forEach((colName: string, idx: number) => {
    if (tableColumns[colName]) {
      visible_columns[colName] = {
        ...tableColumns[colName],
        order: orderMap?.[colName]?.order ?? idx,
      };
    }
  });
  return visible_columns;
}

/**
 * Get saved columns with auto-migration from JSON to comma-separated format
 */
export async function getSavedColumns(options: ColumnSettingsOptions): Promise<dynamicObj> {
  const { settingKey, tableColumns, defaultFilter } = options;

  try {
    const saved_columns = await getColumnFromServer(settingKey);

    if (!saved_columns || saved_columns === '') {
      return prepareDefaultColumns(tableColumns, defaultFilter);
    }

    let colNames: string[];
    let orderMap: dynamicObj | undefined;

    // Check if saved columns are in old JSON format
    if (saved_columns.trim().includes('{')) {
      const parsedColumns = JSON.parse(saved_columns);
      // Auto-migrate: convert to comma-separated and save
      await saveColumnsToServer(settingKey, parsedColumns);
      colNames = Object.keys(parsedColumns);
      orderMap = parsedColumns;
    } else {
      colNames = saved_columns.split(',');
    }

    const visible_columns = buildVisibleColumns(colNames, tableColumns, orderMap);

    // If no columns matched, return defaults
    if (Object.keys(visible_columns).length === 0) {
      return prepareDefaultColumns(tableColumns, defaultFilter);
    }

    return visible_columns;
  } catch (err) {
    return prepareDefaultColumns(tableColumns, defaultFilter);
  }
}

/**
 * Save column order (for drag-drop reordering)
 */
export async function saveColumnOrder(settingKey: string, columns: any[]): Promise<void> {
  await saveColumnsToServer(settingKey, columns);
}
